/**
 * Personal Taška★ offer@SP bag grain keys for bag-in pressed state (Wave 5).
 * Guest: pendingOfferFavorites localStorage. Signed-in: L3 offer-favorites list.
 * Never uses SP ★ / favoriteSalesPointIds.
 */

import { useMemo, useSyncExternalStore } from 'react';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import {
  getPendingOfferFavoritesServerSnapshot,
  getPendingOfferFavoritesSnapshot,
  readPendingOfferFavorites,
  subscribePendingOfferFavorites,
} from '../account/pendingOfferFavorites.js';
import { useOfferFavoritesByToken } from '../account/useOfferFavoritesByToken.js';
import {
  offerFavoriteGrainKey,
  type OfferFavoriteGrainKey,
} from '../account/offerFavoritesTypes.js';

export type PersonalOfferFavoriteKeysState = {
  readonly bagGrainKeys: ReadonlySet<string>;
  readonly isLoading: boolean;
  readonly hasGrain: (grain: OfferFavoriteGrainKey) => boolean;
};

function guestBagKeysFromSnapshot(snapshot: string): ReadonlySet<string> {
  if (snapshot.length === 0) {
    return new Set();
  }
  const keys = new Set<string>();
  for (const item of readPendingOfferFavorites()) {
    if (typeof item.salesPointId !== 'number' || item.salesPointId <= 0) {
      continue;
    }
    keys.add(
      offerFavoriteGrainKey({
        salesPointId: item.salesPointId,
        productId: item.productId,
        variantId: item.variantId ?? null,
      }),
    );
  }
  return keys;
}

export function usePersonalOfferFavoriteKeys(): PersonalOfferFavoriteKeysState {
  const { session } = useCustomerSession();
  const signedIn = isGloballySignedInChrome({ session });
  const bagQuery = useOfferFavoritesByToken(signedIn);

  const guestSnapshot = useSyncExternalStore(
    subscribePendingOfferFavorites,
    getPendingOfferFavoritesSnapshot,
    getPendingOfferFavoritesServerSnapshot,
  );

  const guestKeys = useMemo(
    () => guestBagKeysFromSnapshot(guestSnapshot),
    [guestSnapshot],
  );

  const signedInKeys = useMemo(() => {
    if (!signedIn) {
      return new Set<string>();
    }
    const keys = new Set<string>();
    for (const item of bagQuery.data?.items ?? []) {
      if (!item.listable) {
        continue;
      }
      keys.add(
        offerFavoriteGrainKey({
          salesPointId: item.salesPointContext.salesPointId,
          productId: item.productContext.productId,
          variantId: item.productContext.variantId,
        }),
      );
    }
    return keys;
  }, [bagQuery.data?.items, signedIn]);

  const bagGrainKeys = signedIn ? signedInKeys : guestKeys;
  const isLoading = signedIn && bagQuery.isPending && bagQuery.fetchStatus !== 'idle';

  return {
    bagGrainKeys,
    isLoading,
    hasGrain: (grain) => bagGrainKeys.has(offerFavoriteGrainKey(grain)),
  };
}
